import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotificationMarksAsReadMutation } from "web_myplann/Api/Notifications/useNotificationMarksAsReadMutation";
import { useQuestionnaireAccessMutation } from "web_myplann/Api/Nsure/useQuestionnaireAccessMutation";
import {
	NotificationType,
	useGetDataByType,
} from "web_myplann/Dashboard/Hooks/useGetIconByType";
import {
	type NotificationListItemResponseDTO,
	NotificationTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { device, NavigationTypes } from "web_myplann/Utils/device";

export const useNotificationClick = ({
	type,
	id,
	sourceId,
	customContent,
}: Pick<
	NotificationListItemResponseDTO,
	"type" | "id" | "sourceId" | "customContent"
>) => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(false);

	const { mutateAsync: markAsReadAsync } = useNotificationMarksAsReadMutation();
	const { mutateAsync: getQuestionnaireAccess } =
		useQuestionnaireAccessMutation();

	const { type: notificationType } = useGetDataByType(type, "");

	const isGroupedNotification =
		type === NotificationTypeEnum.ActivityGroup ||
		type === NotificationTypeEnum.TaskGroup;

	const markGroupedAsRead = async () => {
		if (Array.isArray(customContent?.notificationIds)) {
			await Promise.all(
				customContent.notificationIds.map((notificationId) =>
					markAsReadAsync(notificationId as number),
				),
			);
		}
	};

	const handleClick = async () => {
		setIsLoading(true);
		try {
			try {
				if (isGroupedNotification) {
					await markGroupedAsRead();
				} else {
					await markAsReadAsync(id);
				}
			} catch (error) {
				console.error(t(Resources.Notifications.markAsReadFailed), error);
			}

			if (notificationType === NotificationType.meetingRecord) {
				if (customContent?.url && typeof customContent.url === "string") {
					await device.navigationRequest(
						NavigationTypes.ZZJ,
						customContent?.url,
					);
				}
				return;
			}
			if (notificationType === NotificationType.investmentQuestionnaire) {
				if (
					customContent?.identificationNumber &&
					typeof customContent.identificationNumber === "string" &&
					customContent?.clientType &&
					typeof customContent.clientType === "string"
				) {
					const result = await getQuestionnaireAccess({
						identificationNumber: customContent.identificationNumber,
						clientType: customContent.clientType,
					});
					const domain = new URL(result.url).hostname;

					await device.openExternalUrl(result.url, [
						{
							name: "clientZoneExtToken",
							value: result.accessToken,
							domain,
							path: "/",
							secure: true,
						},
					]);
				}
				return;
			}
			if (notificationType === NotificationType.task) {
				navigate(Routing.TaskDetail.path(sourceId ?? ""));
			} else {
				navigate(Routing.ActivityDetail.path(sourceId ?? ""));
			}
		} catch (error) {
			console.error("Failed to handle notification click", error);
		} finally {
			setIsLoading(false);
		}
	};

	return { handleClick, isLoading };
};
